import React, { useCallback, useMemo, useRef, useState } from "react";
import { withRouter } from "react-router-dom";
import api from "../../services/api";
import GenericUpper from "../../components/generic-upper";
import { Container } from "react-grid-system";
import TableWithLabelBlackV2 from '../../components/TableWithLabelBlack/indexV2';
import TextOrStatus from "../../components/textOrStatus";
import IconOrder from '../../assets/icons/icon-order.png';
import status3 from "../../assets/icons/icon-status-enviado.svg";
import { TaglineP } from "../../styles/stylesTypography";
import { theme } from "../../styles/theme";
import maintainUtm from "../../services/maintainUtm";

const STATUS_FILTER_OPTIONS = [
  {
    label: "ATIVO",
    value: "Ativo",
    color: "#03903E",
    backgroundColor: "#EBF9F1",
    icon: status3,
    padding: "5px 8px",
    minHeight: "28px",
  },
  {
    label: "INATIVO",
    value: "Inativo",
    color: "#FF3B30",
    backgroundColor: "#FFEDEE",
    dotColor: "#FF5A52",
    padding: "5px 8px",
    minHeight: "28px",
  },
];

const FILTER_FIELD_MAP = {
  status: "status",
  nome: "nome",
  documento: "numero_art",
  tipo: "tipo",
  ufRegistro: "estado",
};

const normalizeApiFilterValue = (value) => String(value ?? "").trim();
const isEmptyMode = (mode) => mode === "isEmpty" || mode === "isNotEmpty";
const mapStatusFilterValueToApi = (value) => {
  const normalized = normalizeApiFilterValue(value).toLowerCase();
  if (normalized === "ativo" || normalized === "1") return 1;
  if (normalized === "inativo" || normalized === "0") return 0;
  return normalizeApiFilterValue(value);
};

const buildConstraint = (matchMode, value) => {
  const normalizedValue = normalizeApiFilterValue(value);
  if (!isEmptyMode(matchMode) && !normalizedValue) return null;
  return { matchMode, value: normalizedValue };
};

const buildApiFiltersPayload = (columnFilters = {}) => {
  return Object.entries(columnFilters).reduce((acc, [columnKey, state]) => {
    const apiField = FILTER_FIELD_MAP[columnKey];
    if (!apiField || !state) return acc;

    if (columnKey === "status") {
      const selectedValues = Array.isArray(state.selectedValues)
        ? state.selectedValues
          .map((value) => mapStatusFilterValueToApi(value))
          .filter((value) => value !== null && value !== undefined && value !== "")
        : [];
      if (selectedValues.length === 0) return acc;

      acc[apiField] = {
        operator: selectedValues.length > 1 ? "or" : "and",
        constraints: selectedValues.map((value) => ({ matchMode: "contains", value })),
      };
      return acc;
    }

    const constraints = [];
    const sharedValueForEmptyMode = isEmptyMode(state.mode)
      ? (state.secondaryValue ?? state.value)
      : state.value;
    const primaryConstraint = buildConstraint(state.mode, sharedValueForEmptyMode);
    if (primaryConstraint) constraints.push(primaryConstraint);

    const secondaryConstraint = buildConstraint(state.secondaryMode, state.secondaryValue);
    if (secondaryConstraint) constraints.push(secondaryConstraint);
    if (constraints.length === 0) return acc;

    acc[apiField] = {
      operator: state.combine === "or" ? "or" : "and",
      constraints,
    };
    return acc;
  }, {});
};

const getArtsSP = async ({ limit = 100, offset = 0, filters = {} }) => {
  const payload = { search: "", estado: "SP", filters };
  const response = await api.post(`/art/pesquisa?limit=${limit}&offset=${offset}`, payload);
  const data = response?.data?.data;
  const arts = data?.arts || data || [];
  return Array.isArray(arts) ? arts : [];
};

const getRowStatusLabel = (row) => ((row?.status === 1 || row?.crea?.status === 1) ? "Ativo" : "Inativo");
const normalizeFilterValue = (value) => String(value ?? "").trim().toLowerCase();

const getColumnFilterValue = (row, columnKey) => {
  switch (columnKey) {
    case "status":
      return getRowStatusLabel(row);
    case "nome":
      return row?.crea?.agronomo?.nome || "";
    case "documento":
      return row?.numero_art || "";
    case "tipo":
      return row?.crea?.tipo || "";
    case "ufRegistro":
      return row?.crea?.estado || row?.estado || "";
    default:
      return "";
  }
};

const applyArtsFilters = (items, columnFilters) => {
  if (!columnFilters || Object.keys(columnFilters).length === 0) return items;

  const matchText = (rowValue, mode, filterValue) => {
    const normalizedRow = normalizeFilterValue(rowValue);
    const normalizedFilter = normalizeFilterValue(filterValue);
    if (!normalizedFilter) return true;

    switch (mode) {
      case "equals":
        return normalizedRow === normalizedFilter;
      case "notEquals":
        return normalizedRow !== normalizedFilter;
      case "startsWith":
        return normalizedRow.startsWith(normalizedFilter);
      case "endsWith":
        return normalizedRow.endsWith(normalizedFilter);
      case "notContains":
        return !normalizedRow.includes(normalizedFilter);
      case "contains":
      default:
        return normalizedRow.includes(normalizedFilter);
    }
  };

  return items.filter((row) => (
    Object.entries(columnFilters).every(([columnKey, state]) => {
      if (!state) return true;
      const rowValue = getColumnFilterValue(row, columnKey);

      if (columnKey === "status") {
        const selectedValues = Array.isArray(state.selectedValues) ? state.selectedValues : [];
        if (selectedValues.length === 0) return true;
        const normalizedRow = normalizeFilterValue(rowValue);
        return selectedValues.some((value) => normalizedRow.includes(normalizeFilterValue(value)));
      }

      return matchText(rowValue, state.mode, state.value);
    })
  ));
};

const ExportarDefesasSpDetails = ({ history }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [columnFilters, setColumnFilters] = useState({});
  const lastFilterKeyRef = useRef("");
  const requestSeqRef = useRef(0);

  const loadItems = useCallback(async (nextColumnFilters = {}, showLoader = true) => {
    const requestId = ++requestSeqRef.current;
    if (showLoader) setLoading(true);
    try {
      const filters = buildApiFiltersPayload(nextColumnFilters);
      const arts = await getArtsSP({ limit: 100, offset: 0, filters });
      if (requestId !== requestSeqRef.current) return;
      setItems(applyArtsFilters(arts, nextColumnFilters));
    } catch (e) {
      if (requestId !== requestSeqRef.current) return;
      setItems([]);
    }
    if (showLoader && requestId === requestSeqRef.current) setLoading(false);
  }, []);

  React.useEffect(() => {
    loadItems({}, true);
  }, [loadItems]);

  const handleColumnFiltersChange = useCallback((nextFilters) => {
    setColumnFilters((previousFilters) => {
      const previousKey = JSON.stringify(previousFilters ?? {});
      const nextKey = JSON.stringify(nextFilters ?? {});
      return previousKey === nextKey ? previousFilters : (nextFilters ?? {});
    });
  }, []);

  React.useEffect(() => {
    const nextKey = JSON.stringify(columnFilters || {});
    const previousKey = lastFilterKeyRef.current;
    if (nextKey === previousKey) return;
    lastFilterKeyRef.current = nextKey;
    const hasActiveFiltersNow = nextKey !== "{}" && nextKey !== "";
    const hadActiveFiltersBefore = previousKey !== "" && previousKey !== "{}";
    if (!hasActiveFiltersNow && !hadActiveFiltersBefore) return;

    const timer = setTimeout(() => {
      loadItems(columnFilters, true);
    }, 2000);

    return () => clearTimeout(timer);
  }, [columnFilters, loadItems]);

  const onSelectRow = useCallback((id) => {
    history.push({
      pathname: `/contracts/${id}`,
      search: maintainUtm(),
    });
  }, [history]);

  const columns = useMemo(() => ([
    {
      key: "status",
      label: "Status",
      minWidth: "110px",
      width: "100%",
      maxWidthContent: "100%",
      enableHorizontalScroll: true,
      filterType: "checkbox",
      filterPanelWidth: "200px",
      filterOptions: STATUS_FILTER_OPTIONS,
      filterBy: (row) => getRowStatusLabel(row),
      render: (row) => (
        <TextOrStatus
          maxWidth="auto"
          type="status"
          status={(row?.status === 1 || row?.crea?.status === 1) ? 2 : 3}
          textSuccess="Ativo"
          textError="Inativo"
          textDefault="Ativo"
        />
      ),
    },
    {
      key: "nome",
      label: "Nome",
      minWidth: "260px",
      width: "280px",
      filterBy: (row) => row?.crea?.agronomo?.nome || "",
      render: (row) => (
        <TaglineP color={theme.colors.gray.Cinza_2} fontWeight={800}>
          {row?.crea?.agronomo?.nome || "-"}
        </TaglineP>
      ),
    },
    {
      key: "documento",
      label: "Documento",
      minWidth: "170px",
      width: "190px",
      filterBy: (row) => row?.numero_art || "",
      render: (row) => (
        <TaglineP color={theme.colors.gray.Cinza_2} fontWeight={600} style={{ overflowWrap: "anywhere" }}>
          {row?.numero_art || "-"}
        </TaglineP>
      ),
    },
    {
      key: "tipo",
      label: "Tipo",
      minWidth: "100px",
      width: "120px",
      filterBy: (row) => row?.crea?.tipo || "",
      render: (row) => (
        <TaglineP color={theme.colors.gray.Cinza_2} fontWeight={600} style={{ overflowWrap: "anywhere" }}>
          {(row?.crea?.tipo || "-")?.toString().toUpperCase()}
        </TaglineP>
      ),
    },
    {
      key: "ufRegistro",
      label: "UF/Registro",
      minWidth: "110px",
      width: "130px",
      filterBy: (row) => row?.crea?.estado || "",
      render: (row) => (
        <TaglineP color={theme.colors.gray.Cinza_2} fontWeight={600} style={{ overflowWrap: "anywhere" }}>
          {(row?.crea?.estado || "-")?.toString().toUpperCase()}
        </TaglineP>
      ),
    },
  ]), []);

  return (
    <>
      <GenericUpper title="Exportar Defesas - SP" backLink="/exportar-defesas" />
      <Container style={{ paddingLeft: "32px", paddingRight: "32px" }}>
        <div style={{ maxWidth: '820px', margin: '16px auto 8px', textAlign: 'center' }}>
          <TaglineP color={theme.colors.gray.Cinza_2} fontWeight={800}>
            Selecione a ART para acessar os contratos e exportar o relatório ao CREA-SP.
          </TaglineP>
          <p style={{ fontSize: '14px', marginTop: '8px' }}>
            <a
              href="https://www.creasp.org.br/arquivos/crea_inf/arquivos/art-livreto-web.pdf"
              target="_blank"
              rel="noreferrer noopener"
            >
              Ver orientações oficiais do CREA-SP
            </a>
          </p>
        </div>

        <TableWithLabelBlackV2
          columns={columns}
          data={items}
          showAll={true}
          itemsPerPage={items.length}
          serverSide={true}
          isLoading={loading}
          enableColumnFilters={true}
          useServerSideColumnFilters={true}
          onColumnFiltersChange={handleColumnFiltersChange}
          enableDefaultRowNavigation={false}
          emptyMessage="Não há resultados"
          actionsColumn={{
            enabled: true,
            position: "first",
            label: "AÇÕES",
            width: "100%",
            minWidth: "80px",
            maxWidth: "80px",
            justifyContent: "flex-start",
            buttons: [
              {
                key: "report",
                icon: IconOrder,
                iconAlt: "Ícone de relatório",
                tooltip: "Ver contratos",
                ariaLabel: "Ver contratos",
                onClick: (row) => onSelectRow(row?.id),
              },
            ],
          }}
          getRowId={(row) => row?.id}
        />
      </Container>
    </>
  );
};

export default withRouter(ExportarDefesasSpDetails);
